import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Paper, Typography, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SessionReplacementDialog, { SessionReplacementProof, SessionReplacementProofType } from '../components/SessionReplacementDialog';

const ParentLogin: React.FC = () => {
  const [studentId, setStudentId] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [replacementChallenge, setReplacementChallenge] = useState<string | null>(null);
  const [replacementProofType, setReplacementProofType] = useState<SessionReplacementProofType>('parentPhone');
  const { parentLogin, confirmSessionReplacement } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const submit = async () => {
    setError(null);
    setLoading(true);
    try {
      await parentLogin(studentId.trim(), parentPhone.trim());
      navigate('/parent/dashboard');
    } catch (err: any) {
      if (err?.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(err.response.data.challengeToken);
        setReplacementProofType(err.response.data.proofType || 'parentPhone');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Login failed');
      }
      setLoading(false);
    }
  };

  const confirmReplacement = async (proof: SessionReplacementProof) => {
    if (!replacementChallenge) return;
    setLoading(true);
    setError(null);
    try {
      await confirmSessionReplacement(replacementChallenge, proof);
      navigate('/parent/dashboard');
    } catch (err: any) {
      if (err?.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(err.response.data.challengeToken);
        setReplacementProofType(err.response.data.proofType || replacementProofType);
        setError(null);
      } else if (err?.response?.data?.code === 'INVALID_CONFIRMATION_CREDENTIALS') {
        setError(err.response.data.message || 'The parent phone number is incorrect.');
      } else if (err?.response?.data?.code === 'SESSION_REPLACEMENT_EXPIRED') {
        setReplacementChallenge(null);
        setError(err.response.data.message || 'The sign-in confirmation expired. Please sign in again.');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Could not sign in on this device');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
      <Paper sx={{ p: 3, width: 420 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Parent / Guardian Login
        </Typography>
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label="Student ID"
          fullWidth
          value={studentId}
          onChange={(e) => setStudentId(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Parent Phone Number"
          fullWidth
          value={parentPhone}
          onChange={(e) => setParentPhone(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="contained" onClick={submit} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>
          <Button variant="text" onClick={() => navigate('/high-school/login')}>
            Student login
          </Button>
        </Box>
      </Paper>
      <SessionReplacementDialog
        open={Boolean(replacementChallenge)}
        proofType={replacementProofType}
        loading={loading}
        error={error}
        onConfirm={confirmReplacement}
        onCancel={() => { setReplacementChallenge(null); setError(null); }}
      />
    </Box>
  );
};

export default ParentLogin;
